import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CopyOutlined, DeleteOutlined, EditOutlined, EyeOutlined, PlayCircleOutlined, RedoOutlined, ExportOutlined, FileTextOutlined } from '@ant-design/icons';
import { Button, Card, Input, List, Modal, Progress, Space, Tag, Typography, message } from 'antd';
import { deleteIndex, duplicateGraph, startIndex, updateGraphTitle, exportGraphDirect, getStatisticalExportStatus, getGraphPapers, renameGraphPaper } from '../services/api';
import ShareButton from './ShareTab';
import StatisticalExportButton from './StatisticalExportButton';
import './JobTab.css';

const { Text, Title } = Typography;

const statusColorMap = {
  ready: 'success',
  pending: 'default',
  importing: 'default',
  failed: 'error',
  running: 'processing',
  scheduled: 'processing',
};

const modelLabelMap = {
  'gpt-6-astra': 'GPT-6 Astra',
  'gpt-6.1-sol': 'GPT-6.1 Sol',
  'claude-opus-5-5': 'Claude Opus 5.5',
  'claude-fable-5': 'Claude Fable 5 (legacy)',
  'grok-4.6': 'Grok 4.6',
  'gemini-3.8-flash': 'Gemini 3.8 Flash',
  'mistral-medium-3-5': 'Mistral Medium 3.5',
  'DeepSeek-V4.1-Flash': 'DeepSeek V4.1 Flash',
  'qwen--qwen38-27b': 'Qwen 3.8 27B',
  'kimi-k3': 'Kimi K3',
  'gpt-5.6-sol': 'GPT-5.6 Sol',
};

const workflowState = (workflow, completedWorkflows, failedWorkflows) => {
  if (completedWorkflows.includes(workflow)) return { label: workflow, color: 'green' };
  if (failedWorkflows.includes(workflow)) return { label: workflow, color: 'red' };
  return { label: workflow, color: 'blue' };
};

export function JobTab({ job, setGraphId, onRefresh, setJobs }) {
  const {
    id,
    uuid,
    progress,
    percent_complete,
    all_workflows = [],
    completed_workflows = [],
    failed_workflows = [],
  } = job;
  const [previewReady, setPreviewReady] = useState(false);
  useEffect(() => {
    let cancelled=false, timer;
    const refresh=async () => {
      try {
        const state=await getStatisticalExportStatus(job.id);
        if (cancelled) return;
        setPreviewReady(Boolean(state.preview_ready));
        if (!state.preview_ready && ['running','scheduled'].includes(job.status)) timer=setTimeout(refresh,15000);
      } catch {}
    };
    if (job.extraction_version==='v2') refresh();
    return () => { cancelled=true; clearTimeout(timer); };
  }, [job.id,job.status,job.extraction_version]);
  const [status, setStatus] = useState(job.status);
  const [title, setTitle] = useState(job.title);
  const navigate = useNavigate();
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [isTitleModalOpen, setIsTitleModalOpen] = useState(false);
  const [titleDraft, setTitleDraft] = useState(job.title || '');
  const [isExporting, setIsExporting] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [isPaperListOpen, setIsPaperListOpen] = useState(false);
  const [isLoadingPapers, setIsLoadingPapers] = useState(false);
  const [papers, setPapers] = useState([]);
  const displayId = job.display_id || uuid?.split('-')[0] || id;
  const canManage = job.can_manage !== false;
  const displayedTitle = /^Graph \d+$/.test(title || '') ? `Graph ${displayId}` : title;
  const generationModelLabel = modelLabelMap[job.completion_model] || job.completion_model || 'Not recorded';
  const ownerLabel = job.owner_display_name || job.owner_username || 'Public';
  const sharedWithLabels = (
    job.shared_with_display_names?.length
      ? job.shared_with_display_names
      : (job.shared_with || [])
  );

  useEffect(() => {
    setStatus(job.status);
  }, [job.status]);

  const handleDelete = async () => {
    const confirmed = await new Promise((resolve) => {
      Modal.confirm({
        title: 'Delete this graph job?',
        content: `Graph ${displayId} and its associated graph data will be removed.`,
        okText: 'Delete',
        okButtonProps: { danger: true },
        cancelText: 'Cancel',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      })
    });

    if (!confirmed) {
      return;
    }

    try {
      setJobs((current) => current.filter((job) => job.id !== id))
      await deleteIndex(id);
      message.success('Job deleted.');
    } catch (error) {
      console.error('Error deleting job:', error);
      message.error('Failed to delete job due to an error.');
    }
  };

  const handleViewGraph = () => {
    setGraphId(id, job.chat_model, displayedTitle);
    const graphAddressId = uuid?.split('-').pop() || id;
    navigate(`/graph/${encodeURIComponent(graphAddressId)}`);
  };

  const handleStartIndex = async () => {
    try {
      await startIndex(id);
      message.success(status === 'failed' ? 'Indexing restarted.' : 'Indexing started successfully.');
      setStatus('scheduled');
      setJobs((current) => current.map((currentJob) => (
        currentJob.id === id ? { ...currentJob, status: 'scheduled', percent_complete: 0 } : currentJob
      )));
    } catch (error) {
      console.error('Error starting index:', error);
      message.error('Failed to start indexing due to an error.');
    }
  };

  const handleOpenPapers = async () => {
    setIsPaperListOpen(true);
    setIsLoadingPapers(true);
    try {
      setPapers(await getGraphPapers(uuid));
    } catch (error) {
      console.error('Error loading graph papers:', error);
      message.error('Failed to load paper list.');
    } finally {
      setIsLoadingPapers(false);
    }
  };

  const handlePaperRename = async (paper, nextName) => {
    const name = nextName.trim();
    if (!name || name === paper.name) return;
    try {
      const updated = await renameGraphPaper(paper.id, name);
      setPapers((current) => current.map((item) => (
        item.id === paper.id ? { ...item, name: updated.name } : item
      )));
      message.success('Paper name updated.');
    } catch (error) {
      console.error('Error renaming paper:', error);
      message.error(error.message || 'Failed to rename paper.');
    }
  };

  const handleTitleChange = async (nextTitle) => {
    const trimmedTitle = nextTitle.trim();
    const currentTitle = (title || `Graph ${displayId}`).trim();

    if (!trimmedTitle) {
      message.warning('Graph title cannot be blank.');
      return false;
    }
    if (trimmedTitle === currentTitle) {
      return true;
    }
    setIsSavingTitle(true);
    try {
      await updateGraphTitle(id, trimmedTitle);
      setTitle(trimmedTitle);
      setJobs((current) => current.map((currentJob) => (
        currentJob.id === id ? { ...currentJob, title: trimmedTitle } : currentJob
      )));
      message.success('Graph title updated.');
      return true;
    } catch (error) {
      console.error('Error updating graph title:', error);
      message.error('Failed to update graph title.');
      return false;
    } finally {
      setIsSavingTitle(false);
    }
  };

  const openTitleEditor = () => {
    setTitleDraft(title || `Graph ${displayId}`);
    setIsTitleModalOpen(true);
  };

  const saveTitle = async () => {
    const saved = await handleTitleChange(titleDraft);
    if (saved) setIsTitleModalOpen(false);
  };

  const exportFiles = async () => {
    setIsExporting(true);
    const hide = message.loading('Preparing export...', 0);
    try {
      const { downloadUrl, filename } = await exportGraphDirect(id, {
        onStatus: (s) => {
          // Could surface phase here if desired; for now the loading toast is enough.
        },
      });
      hide();
      const link = document.createElement('a');
      link.href = downloadUrl;
      if (filename) link.download = filename;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success('Export ready. Download started.');
    } catch (error) {
      hide();
      console.error('Export failed:', error);
      message.error(error.message || 'Failed to export graph.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDuplicate = async () => {
    const confirmed = await new Promise((resolve) => {
      Modal.confirm({
        title: 'Duplicate this graph?',
        content: 'Graph data and paper metadata will be copied to a new graph ID. PDFs and chat history will not be copied, and the graph will not be regenerated.',
        okText: 'Duplicate',
        cancelText: 'Cancel',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!confirmed) return;

    setIsDuplicating(true);
    try {
      await duplicateGraph(id);
      message.success('Graph duplication started.');
      await onRefresh();
    } catch (error) {
      console.error('Graph duplication failed:', error);
      message.error(error.message || 'Failed to duplicate graph.');
    } finally {
      setIsDuplicating(false);
    }
  };

  const workflowTags = all_workflows.map((workflow) => workflowState(workflow, completed_workflows, failed_workflows));

  return (
    <Card bordered={false} className="job-tab-card" title={
      <div className="job-tab-card__header">
        <div className="job-tab-card__title-group">
        <Title
          level={5}
          className="job-tab-card__title"
          style={{ margin: 0, color: '#fff'}}
          title={displayedTitle}
        >
          {displayedTitle}
        </Title>
          {canManage && (
            <Button
              type="text"
              size="small"
              className="job-tab-card__edit-title"
              icon={<EditOutlined />}
              aria-label="Edit title"
              title="Edit title"
              onClick={openTitleEditor}
            />
          )}
        </div>
        <Tag color={statusColorMap[status] || 'default'}>{status.toUpperCase()}</Tag>
      </div>
      } styles={{header: { backgroundColor: '#4f5471'}}}>
      <Space orientation="vertical" size={16} style={{ width: '100%', flex: 1 }}>

        <Space orientation="vertical" size={8}>
          <Space size={8} wrap>
            <Text type="secondary">Generation model</Text>
            <Tag color="geekblue" bordered={false}>{generationModelLabel}</Tag>
            <Tag color={job.extraction_version === 'v2' ? 'purple' : 'default'}>{job.workflow_version || job.extraction_version || 'v1'}</Tag>
          </Space>
          <Space size={8} wrap>
            <Text type="secondary">Owner</Text>
            <Text>{ownerLabel}</Text>
          </Space>
          {sharedWithLabels.length > 0 && (
            <Space size={8} wrap>
              <Text type="secondary">Shared with</Text>
              <Text>{sharedWithLabels.join(', ')}</Text>
            </Space>
          )}
        </Space>

        <Progress percent={Number(percent_complete) || 0} strokeColor="#0f766e" trailColor="rgba(15, 118, 110, 0.08)" />
        <Text type="secondary">
          {isSavingTitle ? 'Saving title...' : progress}
        </Text>

        <Space size={[8, 8]} wrap>
          {workflowTags.map((workflow) => (
            <Tag key={workflow.label} color={workflow.color} bordered={false}>
              {workflow.label}
            </Tag>
          ))}
        </Space>

        <div className="job-tab-card__actions">
          {status !== 'ready' && previewReady && <Button type="primary" icon={<EyeOutlined />} onClick={handleViewGraph}>Preview stats graph</Button>}
          {status === 'ready' && (
            <Button type="primary" icon={<EyeOutlined />} onClick={handleViewGraph}>
              View graph
            </Button>
          )}
          {status === 'ready' && (
            <Button icon={<ExportOutlined />} onClick={exportFiles} loading={isExporting} disabled={isExporting}>
              {isExporting ? 'Exporting...' : 'Export'}
            </Button>
          )}
          <ShareButton
            jobId={job.uuid}
            canManage={canManage}
            publicAccess={Boolean(job.public_access)}
          />
          <Button icon={<FileTextOutlined />} onClick={handleOpenPapers}>
            Papers
          </Button>
          {status === 'pending' && canManage && (
            <Button icon={<PlayCircleOutlined />} onClick={handleStartIndex}>
              Start indexing
            </Button>
          )}
          {status === 'failed' && canManage && (
            <Button icon={<RedoOutlined />} onClick={handleStartIndex}>
              Retry
            </Button>
          )}
          {status === 'ready' && (
            <Button icon={<CopyOutlined />} onClick={handleDuplicate} loading={isDuplicating} disabled={isDuplicating}>
              Duplicate
            </Button>
          )}
          {status !== 'importing' && canManage && (
            <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
              Delete
            </Button>
          )}
          {job.extraction_version === 'v2' && (
            <StatisticalExportButton className="job-tab-card__statistical-export" graphId={id} jobStatus={status} />
          )}
        </div>
      </Space>
      <Modal
        title="Edit graph title"
        open={isTitleModalOpen}
        onOk={saveTitle}
        onCancel={() => setIsTitleModalOpen(false)}
        okText="Save"
        confirmLoading={isSavingTitle}
        destroyOnHidden
      >
        <Input
          value={titleDraft}
          onChange={(event) => setTitleDraft(event.target.value)}
          onPressEnter={saveTitle}
          maxLength={255}
          showCount
          autoFocus
        />
      </Modal>
      <Modal
        title={`Papers in ${displayedTitle}`}
        open={isPaperListOpen}
        onCancel={() => setIsPaperListOpen(false)}
        footer={null}
      >
        <List
          loading={isLoadingPapers}
          dataSource={papers}
          locale={{ emptyText: 'No papers recorded for this graph.' }}
          renderItem={(paper) => (
            <List.Item>
              <Space orientation="vertical" size={2} style={{ width: '100%' }}>
                {paper.source === 'publication' && paper.url ? (
                  <Typography.Link href={paper.url} target="_blank" rel="noopener noreferrer">
                    {paper.name}
                  </Typography.Link>
                ) : (
                  <Text
                    editable={paper.can_rename ? {
                      onChange: (nextName) => handlePaperRename(paper, nextName),
                      maxLength: 255,
                    } : false}
                  >
                    {paper.name}
                  </Text>
                )}
                <Text type="secondary">
                  {paper.source === 'publication' ? 'Data Vault publication' : paper.file_name}
                </Text>
                {paper.metadata?.citation && (
                  <Typography.Paragraph copyable style={{ marginBottom: 0 }}>
                    {paper.metadata.citation}
                  </Typography.Paragraph>
                )}
                {paper.metadata?.status && paper.metadata.status !== 'resolved' && (
                  <Text type="secondary">
                    {paper.metadata.status === 'ambiguous'
                      ? 'Multiple article identifiers found; citation needs review.'
                      : 'APA citation unavailable; no verified article metadata found.'}
                  </Text>
                )}

              </Space>
            </List.Item>
          )}
        />
      </Modal>
    </Card>
  );
}
